import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Paper
} from '@mui/material';
import {
  Favorite,
  Security,
  PhoneAndroid,
  Schedule
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <LandingLayout>
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(129, 199, 132, 0.03) 100%)',
        py: 8,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle wave pattern like Casante */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 80% 20%, rgba(76, 175, 80, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" component="h1" gutterBottom sx={{ 
                fontWeight: 700, 
                color: '#212121',
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2,
                mb: 3
              }}>
                You can't always be there. Casanté can.
              </Typography>
              
              <Typography variant="h4" sx={{ 
                color: '#212121',
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}>
                Transforming homes and lives for long-term independence.
              </Typography>
              
              <Typography variant="h5" sx={{ mb: 4, color: '#757575', lineHeight: 1.6 }}>
                Monitor the activity and health of your loved ones, from anywhere.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', color: '#757575' }}>
                  ✅ No smartphone or app required<br/>
                  ✅ Works with any phone that can receive texts<br/>
                  ✅ Instant notifications to your care circle<br/>
                  ✅ Secure, easy to use platform
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    backgroundColor: '#2E7D32',
                    '&:hover': {
                      backgroundColor: '#1B5E20'
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.2rem'
                  }}
                >
                  Contact Casanté
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/how-it-works')}
                  sx={{
                    borderColor: '#2E7D32',
                    color: '#2E7D32',
                    '&:hover': {
                      borderColor: '#1B5E20',
                      backgroundColor: 'rgba(46, 125, 50, 0.04)'
                    },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.2rem'
                  }}
                >
                  How It Works
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={4} sx={{ p: 4, backgroundColor: '#ffffff', borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  How It Works in 3 Simple Steps:
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      backgroundColor: '#2E7D32', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: 24, 
                      height: 24, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mr: 2, 
                      fontSize: '0.9rem',
                      fontWeight: 'bold' 
                    }}>
                      1
                    </Box>
                    Daily text sent: "Are you okay today?"
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      backgroundColor: '#2E7D32', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: 24, 
                      height: 24, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mr: 2, 
                      fontSize: '0.9rem',
                      fontWeight: 'bold' 
                    }}>
                      2
                    </Box>
                    Your loved one replies "1" or "Yes"
                  </Typography>
                  
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      backgroundColor: '#2E7D32', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: 24, 
                      height: 24, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mr: 2, 
                      fontSize: '0.9rem',
                      fontWeight: 'bold' 
                    }}>
                      3
                    </Box>
                    You get peace of mind (or alerts if needed)
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ 
          color: '#2E7D32', 
          fontWeight: 'bold',
          mb: 6
        }}>
          Why Families Choose Casante - Checks
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <CardContent>
                <Favorite sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Peace of Mind
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Know your loved one is safe with daily check-ins and instant alerts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <CardContent>
                <PhoneAndroid sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Simple & Easy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No apps to download. Works with any phone that can receive texts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <CardContent>
                <Security sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Reliable & Secure
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Professional SMS service with automatic retries and secure data handling
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <CardContent>
                <Schedule sx={{ fontSize: 60, color: '#2E7D32', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Flexible Scheduling
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set convenient check-in times and vacation modes for your family's needs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Pricing Teaser */}
      <Box sx={{ backgroundColor: '#F9F9F9', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ 
            color: '#2E7D32', 
            fontWeight: 'bold',
            mb: 3
          }}>
            Affordable Peace of Mind
          </Typography>
          
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Starting at just <strong>$14.99/month</strong> or save with our annual plan
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                backgroundColor: '#2E7D32',
                '&:hover': {
                  backgroundColor: '#1B5E20'
                },
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                mr: 2,
                mb: 2
              }}
            >
              View Pricing
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                borderColor: '#2E7D32',
                color: '#2E7D32',
                '&:hover': {
                  borderColor: '#1B5E20',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                },
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                mb: 2
              }}
            >
              Get Started
            </Button>
          </Box>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
            No commitment • Cancel anytime • Secure payment processing
          </Typography>
        </Container>
      </Box>
    </LandingLayout>
  );
};

export default LandingPage;
